/**
 * Keying in a supplier's price that did not arrive by email.
 *
 * A supplier quotes over the phone, on WhatsApp, or across a counter at least as often as they
 * return our workbook. Until this existed the only route into a `Supplier Quotation` was parsing
 * an inbound mail against an RFQ, so those prices had nowhere to go — the comparison never saw
 * them and the enquiry read as unanswered.
 *
 * ---- What it deliberately does not do ----
 *
 * No RFQ is created, and none is needed: `create_supplier_quotation` accepts an empty `rfq`
 * (verified live). No conversion, no rounding of the rate, no restating into another unit — the
 * number filed is the number the purchaser was told, in the currency they name, against the
 * line's own unit. `compare.ts` converts from raw facts, and doing it twice is how a price drifts
 * from what the supplier actually said.
 *
 * Lines are left BLANK rather than pre-filled with anything. A supplier who quoted three of
 * twenty lines has quoted three; a pre-filled zero, or a copied estimate, becomes an offer of
 * free goods that wins the allocation.
 */

import { printedDescription, withoutInternalAnnotations } from '../../lib/internalNotes'
import { useMemo, useState } from 'react'
import { LineTextarea } from '../../components/LineTextarea'
import { Modal } from '../../components/Modal'
import { Button, Placeholder } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { useCurrencyPicker } from '../../lib/api/useEnquiryFromMail'
import { useExchangeRates } from '../../lib/api/useMasters'
import { pickRate } from '../../lib/exchangeRates'
import { useSuppliers } from '../../lib/api/useSuppliers'
import { isRfqLine } from '../../lib/rfq'
import { matchesLineSearch } from '../../lib/lineSearch'
import { SearchBox } from '../../components/SearchBox'
import type { EnquiryItem } from '../../lib/types'

export function RecordQuotationModal({
  lines,
  emptyHint,
  baseCurrency,
  company,
  saving,
  onSave,
  onClose,
}: {
  lines: EnquiryItem[]
  /**
   * What to say when no line can be priced. The default speaks for the supplier-quotations screen
   * ("match the items first"); the revisions screen opens this for ONE requested item and needs to
   * explain something different.
   */
  emptyHint?: string
  baseCurrency: string
  /** The enquiry's company — scopes the supplier search. Falls back to the session's operating company. */
  company?: string
  saving: boolean
  onSave: (input: {
    supplier: string
    currency: string
    transactionDate: string
    rates: Map<string, number>
    /** `line_id` → what the supplier said beyond the number. See `ManualQuotationInput.remarks`. */
    remarks: Map<string, string>
  }) => void
  onClose: () => void
}) {
  const [supplier, setSupplier] = useState('')
  const [currency, setCurrency] = useState(baseCurrency)
  /*
    Whether this currency can actually be converted to the company's. `pickRate` answers `1` for
    same-currency without needing a row, so the base currency never warns.

    Only once the rates have LOADED — while the query is in flight every currency looks unrateable,
    and flashing a warning on the default selection would be noise on every open.
  */
  const exchangeRates = useExchangeRates()
  const noRateForCurrency =
    exchangeRates.data !== undefined &&
    currency.trim() !== '' &&
    pickRate(exchangeRates.data, currency, baseCurrency) === null
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  /*
    What the supplier said beyond the number — "ex-stock", "6 week lead", "min 10 pcs". A price
    given by phone is exactly where those conditions are stated, and the comparison already renders
    them (`OfferCell`), so recording one without a place to write them lost the half that does not
    fit in a rate.
  */
  const [remarks, setRemarks] = useState<Map<string, string>>(new Map())
  /*
    Narrowing a long enquiry to the lines actually quoted.

    A supplier on the phone prices two items out of seventy-nine; without this the purchaser scrolls
    for them. Filtering only what is DISPLAYED — `rates` and `remarks` are keyed by `line_id` and
    are never touched here, so a price typed, then filtered out of view, is still filed. Clearing
    the box brings it back with its value intact.
  */
  const [search, setSearch] = useState('')
  const setRemark = (lineId: string, text: string) =>
    setRemarks((prior) => new Map(prior).set(lineId, text))

  /*
    A HOOK, called as one by `LinkPicker` (`useOptions(term)` on every render) — which is what lets
    the typed term reach the SERVER.

    This used to call `useSuppliers()` with no term and filter the result client-side. That page is
    200 rows of 2,831, so 2,631 suppliers were simply unreachable: typing "TECHNO M" answered
    "No match." while `masters.get_suppliers?search=techno m` returns both TECHNO Marine records
    at once — the same search Frappe's own list view does (measured 2026-09-07).

    `useSuppliers` keeps the previous list on screen while a new search runs (its own note), so the
    dialog does not blank on every keystroke against 2,831 rows.
  */
  const useSupplierOptions = (search: string) => {
    const q = useSuppliers(search.trim() || undefined, company)
    return {
      // `code` is the Link identity written to the quotation; `name` is only what a person reads.
      data: (q.data?.data ?? []).map((s) => ({ name: s.code, label: s.name })),
      isPending: q.isPending,
    }
  }

  /*
    Only RFQ-routed lines. A contract line is already priced by a blanket order — asking a supplier
    to quote it, or recording a competing price against it, is not what this is for.
  */
  /*
    What the purchaser TYPED, kept verbatim.

    Two bugs came from not holding this. The rate inputs were UNCONTROLLED, so filtering the list
    unmounted a row and it came back blank — a price entered, then searched away from, looked lost
    (reported 2026-09-07). And `setRate` stored only the parsed number, deleting the entry for
    anything `<= 0`, so a typed `0` could not be expressed at all.

    Holding the text also lets a half-typed value stand: `0.` and `1.` parse to numbers but must
    not be rewritten under the cursor.
  */
  const [rateText, setRateText] = useState<Map<string, string>>(new Map())
  const setRate = (lineId: string, value: string) =>
    setRateText((current) => new Map(current).set(lineId, value))

  /*
    Text → the rates that get filed. A BLANK line has no entry (they did not price it); a typed
    zero has an entry of 0 (they quoted zero). That absence-vs-value distinction is the whole
    contract `buildManualQuotation` reads — see its header.
  */
  const rates = useMemo(() => {
    const out = new Map<string, number>()
    for (const [lineId, text] of rateText) {
      if (text.trim() === '') continue
      const n = Number(text)
      if (Number.isFinite(n) && n >= 0) out.set(lineId, n)
    }
    return out
  }, [rateText])

  const quotable = lines.filter((l) => isRfqLine(l) && l.item_code)
  // Display only — see the note on `search`. `priced` counts every rate, not just visible ones.
  const shown = quotable.filter((l) => matchesLineSearch(l, search))
  /* Every line that carries a rate, INCLUDING a quoted zero — the button must not say "0 priced"
     over a supplier who quoted zero on the only line they answered. */
  const priced = rates.size

  return (
    <Modal
      title="Record a supplier's quotation"
      onClose={onClose}
      footer={
        <>
          <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
            {priced} of {quotable.length} line(s) priced
          </span>
          <span style={{ flex: 1 }} />
          <Button
            variant="primary"
            disabled={saving || supplier === '' || priced === 0}
            onClick={() => onSave({ supplier, currency, transactionDate: date, rates, remarks })}
          >
            {saving ? 'Filing…' : `File ${priced} priced line(s)`}
          </Button>
        </>
      }
    >
      <div className="pad" style={{ color: 'var(--muted)', fontSize: 12.5 }}>
        For a price given by phone, WhatsApp or in person — <b>no RFQ is sent</b>. Fill in only the
        lines they actually quoted; a line left blank is one they did not price, which is not the
        same as a price of zero.
      </div>

      {/*
        Pinned while the line list scrolls beneath it. On a fifty-nine line enquiry the supplier
        and currency would otherwise scroll out of view immediately — and they are the context
        every rate below is being typed against.
      */}
      <div
        className="pad"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--panel)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <label className="ef" style={{ minWidth: 240, flex: 1 }}>
          <span>Supplier</span>
          <LinkCombo
            id="record-quote-supplier"
            value={supplier}
            onChange={setSupplier}
            useOptions={useSupplierOptions}
            placeholder="Search suppliers…"
          />
        </label>
        {/*
          A picker over the `Currency` master, not a free-text box.

          It used to be an input that upper-cased whatever was typed, which let a purchaser file a
          supplier quotation against `USDD` or `ARD` — a currency no `Currency Exchange` row can
          ever match, so the comparison would silently fail to convert that supplier's whole column
          and quietly drop them out of the running. Nothing on screen would have said why.

          Same `LinkCombo` + master idiom as the Supplier field beside it, so the two behave alike.
        */}
        <label className="ef" style={{ width: 150 }}>
          <span>Currency</span>
          <LinkCombo
            value={currency}
            onChange={setCurrency}
            useOptions={useCurrencyPicker}
            placeholder="Search currencies…"
          />
          {/*
            The master lists 148 currencies; only 9 have a `Currency Exchange` rate on file
            (measured 2026-09-03). A quotation filed in one of the other 139 cannot be converted,
            so the comparison drops that supplier's column out of the running — and the purchaser
            would find that out much later, on a different screen, with nothing connecting the two.

            Said here, at the moment of choosing, and NOT as a block: the rate may be added later,
            and refusing a price a supplier has actually given would be worse than warning about it.
          */}
          {noRateForCurrency ? (
            <div style={{ fontSize: 11, marginTop: 3, color: 'var(--warn)' }}>
              ⚠ No {currency} → {baseCurrency} rate on file, so this can't be compared until one is
              added.
            </div>
          ) : null}
        </label>
        <label className="ef" style={{ width: 160 }}>
          {/* Their quotation's own date, which may not be today — a price quoted last week is a
              week old, and the comparison shows that age. */}
          <span>Quoted on</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>

      {quotable.length === 0 ? (
        <Placeholder>
          {emptyHint ??
            'No RFQ-routed line has a matched item yet — match the items first, or price them from a contract instead.'}
        </Placeholder>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <caption style={{ captionSide: 'top', padding: '0 0 10px', textAlign: 'left' }}>
              <SearchBox
                value={search}
                onChange={setSearch}
                total={quotable.length}
                shown={shown.length}
                placeholder={`Search ${quotable.length} lines…`}
              />
              {/*
                Said out loud whenever the list is narrowed AND anything is priced: the filter
                hides rows, not prices, and "0 of 2 shown" beside a Save button that will file
                seven is exactly the misreading this prevents.
              */}
              {search.trim() !== '' && priced > 0 ? (
                <div style={{ marginTop: 6, color: 'var(--muted)', fontSize: 11.5 }}>
                  {priced} line(s) priced in total — filtering hides rows, not the prices you have
                  typed.
                </div>
              ) : null}
            </caption>
            <thead>
              <tr>
                <th>Cust. S.No</th>
                <th>Item</th>
                <th className="right">Qty</th>
                <th>UOM</th>
                <th className="right" style={{ width: 120 }}>Their rate</th>
                <th style={{ width: 200 }}>Offer remarks</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((line) => (
                <tr key={line.line_id}>
                  <td className="mono">{line.cust_sno}</td>
                  <td>
                    {/* The line as the purchaser wrote it — the edited description, which is what
                        prints and what the supplier priced; the customer's own wording beneath
                        when it differs (Akhil, 2026-10-16). */}
                    <div>{printedDescription(line)}</div>
                    {withoutInternalAnnotations(line.raw_text) !== printedDescription(line) ? (
                      <div className="cd">{withoutInternalAnnotations(line.raw_text)}</div>
                    ) : null}
                    <div className="cd mono">{line.item_code}</div>
                  </td>
                  <td className="right mono">{line.qty}</td>
                  <td>{line.uom}</td>
                  <td>
                    {/* CONTROLLED — a row filtered out and back must return with its price. */}
                    <input
                      inputMode="decimal"
                      placeholder="—"
                      value={rateText.get(line.line_id) ?? ''}
                      style={{ width: '100%', textAlign: 'right' }}
                      onChange={(e) => setRate(line.line_id, e.target.value)}
                    />
                  </td>
                  <td>
                    {/* Free text, and deliberately not required: most lines carry no condition,
                        and a field that nags would be filled with noise to get past it. */}
                    {/* Controlled for the same reason as the rate beside it. */}
                    {/* Multi-line (Akhil, 2026-09-18): an offer remark is often several — MOQ,
                        lead time, brand — and a one-line box hid all but the first. */}
                    <LineTextarea
                      placeholder={'ex-stock · 6 week lead · min 10'}
                      value={remarks.get(line.line_id) ?? ''}
                      style={{ fontSize: 12.5, resize: 'none' }}
                      onChange={(e) => setRemark(line.line_id, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  )
}
